import { iterate } from "../../graph/paging.ts";
import { MODULE_SCOPES } from "../../auth/config.ts";
import { InteractionRequiredError, NotSignedInError } from "../../auth/broker.ts";
import { GraphError, graphFetch } from "../../graph/client.ts";
import { ensureDirectory, writeFile } from "../../storage/fsa.ts";
import { getCursor, putCursor, itemNeedsExport, putItem, SINGLE_CURSOR } from "../../storage/idb.ts";
import type { RunLogger } from "../../export/run.ts";
import { ExportAbortedError, throwIfAborted } from "../../export/abort.ts";
import {
    withinRange,
    isPathSelected,
    walkScope,
    scopeCovers,
    type DateRange,
    type ContainerSelection
} from "../../export/plan.ts";
import { relativeFolderSegments, fileRelativePath, folderDiskSegments, displayPath } from "./paths.ts";
import { renderOneDriveIndex } from "./index-render.ts";
import type { DriveItemDelta } from "./types.ts";

/**
 * OneDrive export (ARCHITECTURE.md §15 step 7).
 *
 * Architecturally the simplest module so far: `/me/drive/root/delta` walks
 * the *entire* tree — folders and files interleaved — in one paged, delta-
 * enabled stream, so there is exactly one cursor for the whole module
 * (`SINGLE_CURSOR`), not one per container the way Teams (per chat) or Mail
 * (per folder) need. And unlike Mail, there is no client-side path
 * resolution to do at all — see paths.ts.
 *
 * Processed sequentially, one item at a time, like Teams and Mail — not the
 * up-to-8-concurrent downloads §9 describes as the intended ceiling for
 * OneDrive content. That is real, unused headroom, not an oversight: every
 * other module keeps the same "one item finishes and is recorded before the
 * next starts" resume guarantee, and matching it here was judged more
 * valuable than the throughput a worker-pool would add for what personal
 * OneDrive libraries usually are. Worth revisiting if large drives make this
 * module noticeably slower than it needs to be.
 */

const SCOPES = MODULE_SCOPES.onedrive;

/** File downloads between export.log / export.json checkpoints during a run. */
const LOG_BATCH_SIZE = 50;

export interface OneDriveProgress {
    filesDone: number;
    currentLabel?: string;
    /** Bytes written for the file currently downloading. */
    currentBytes: number;
    /** Bytes written across this whole invocation. */
    totalBytes: number;
}

export type OneDriveProgressCallback = (progress: OneDriveProgress) => void;

export interface OneDriveCheckpoint {
    files: number;
    folders: number;
    bytes: number;
}

export interface ExportOneDriveParams {
    runId: string;
    root: FileSystemDirectoryHandle;
    /** From the Scan step: only export files inside these folder paths. */
    selection?: ContainerSelection;
    /** From the Scan step: skip files last modified outside this window. */
    dateRange?: DateRange;
    onProgress?: OneDriveProgressCallback;
    /** Fired each time a folder is created — folders are few enough to log individually. */
    onFolderCreated?: (path: string) => void;
    /** Fired every LOG_BATCH_SIZE files — files are not, so these are batched. */
    onCheckpoint?: (checkpoint: OneDriveCheckpoint) => void;
    /** Aborts at the next clean checkpoint. */
    signal?: AbortSignal;
    logger: RunLogger;
}

export interface OneDriveResult {
    files: number;
    folders: number;
    bytes: number;
    errors: number;
}

export async function exportOneDrive(params: ExportOneDriveParams): Promise<OneDriveResult> {
    const { runId, root, selection, dateRange, onProgress, onFolderCreated, onCheckpoint, signal, logger } =
        params;

    // Both the date window and the folder selection filter *inside* the one
    // drive-wide cursor, so a cursor walked under a narrower scope moved past
    // files that scope skipped. A wider scope re-walks the drive from the
    // start instead (files already exported are still skipped).
    const scope = walkScope(dateRange, selection);
    const stored = await getCursor(runId, "onedrive", SINGLE_CURSOR);
    const cursor = stored && scopeCovers(stored.scope, scope) ? stored : undefined;
    const startUrl = cursor?.deltaLink ?? cursor?.nextLink ?? "/me/drive/root/delta";

    let files = 0;
    let folders = 0;
    let bytes = 0;
    let errors = 0;
    let sinceCheckpoint = 0;
    let deltaLink: string | undefined;

    await logger.info("starting OneDrive delta walk");

    for await (const item of iterate<DriveItemDelta>(startUrl, {
        scopes: SCOPES,
        service: "drive",
        ...(signal ? { signal } : {}),
        // Kept until the walk ends rather than written here: it is only safe
        // to continue from once every file in this pass was handled (see the
        // putCursor below the loop).
        onDeltaLink: (link) => {
            deltaLink = link;
        },
        onNextLink: (nextLink) => {
            void putCursor({ runId, module: "onedrive", subKey: SINGLE_CURSOR, nextLink, done: false, scope });
        }
    })) {
        throwIfAborted(signal);

        if (item.deleted) {
            // Not fetched, and not deleted locally either — same policy as
            // Mail: this is a backup, and preserving what was already
            // captured outranks mirroring a later deletion.
            continue;
        }
        if (!item.name) {
            // The root item itself, or something else with nothing to write —
            // `root` already serves as the "onedrive/" entry point.
            continue;
        }

        const itemId = item.id;
        const segments = relativeFolderSegments(item.parentReference?.path);
        // A file's version is its content tag plus where it lives: changed
        // content is downloaded again over the old copy, and a moved or renamed
        // file is written at its new path (the copy at the old path stays —
        // backup, not mirror). Folders carry none: a folder is done once made.
        const version = item.file
            ? `${item.cTag ?? item.eTag ?? item.lastModifiedDateTime ?? ""}|${fileRelativePath(segments, item.name)}`
            : undefined;
        if (!(await itemNeedsExport(runId, "onedrive", itemId, version))) {
            continue;
        }

        try {
            if (item.folder) {
                // Scan-step selection: only create a folder that is, or lives
                // under, a selected folder. Files pull in their own parents via
                // writeFile, so an unselected folder can simply be skipped.
                if (!isPathSelected([...segments, item.name], selection)) {
                    continue;
                }
                await ensureDirectory(root, folderDiskSegments(segments, item.name));
                await putItem({ runId, module: "onedrive", itemId, status: "done", bytes: 0 });
                folders += 1;
                onFolderCreated?.(displayPath(segments, item.name));
                continue;
            }

            if (!item.file) {
                // Neither a file nor a folder facet — an item type this
                // module doesn't know about yet (e.g. a package/bundle).
                // Recorded as skipped so it is not retried forever.
                await putItem({ runId, module: "onedrive", itemId, status: "skipped", bytes: 0 });
                continue;
            }

            // Scan-step selection (by containing folder) and date filter.
            if (!isPathSelected(segments, selection)) {
                continue;
            }
            if (dateRange && !withinRange(item.lastModifiedDateTime, dateRange)) {
                continue;
            }

            onProgress?.({ filesDone: files, currentLabel: item.name, currentBytes: 0, totalBytes: bytes });

            const response = await graphFetch(`/me/drive/items/${item.id}/content`, {
                scopes: SCOPES,
                service: "drive",
                ...(signal ? { signal } : {})
            });
            const blob = await response.blob();
            const path = fileRelativePath(segments, item.name);
            const written = await writeFile(root, path, blob);
            await putItem({
                runId,
                module: "onedrive",
                itemId,
                status: "done",
                bytes: written,
                ...(version !== undefined ? { version } : {})
            });

            files += 1;
            bytes += written;
            sinceCheckpoint += 1;
            onProgress?.({
                filesDone: files,
                currentLabel: item.name,
                currentBytes: written,
                totalBytes: bytes
            });

            if (sinceCheckpoint >= LOG_BATCH_SIZE) {
                sinceCheckpoint = 0;
                onCheckpoint?.({ files, folders, bytes });
            }
        } catch (err) {
            if (
                err instanceof ExportAbortedError ||
                err instanceof InteractionRequiredError ||
                err instanceof NotSignedInError
            ) {
                throw err;
            }
            // One item's problem alone — logged and left unmarked so a future
            // resume retries it, everything else keeps going.
            errors += 1;
            const message = err instanceof GraphError ? err.toLogString() : String(err);
            await logger.error(`item failed, will retry on resume: ${message} (${itemId})`);
        }
    }

    // The deltaLink is kept only after an error-free pass. A file that failed
    // is not marked done, but a deltaLink only reports *later* changes, so
    // continuing from it would never offer that file again; without one the
    // next run walks from the start and retries it (done ones are skipped).
    // This write used to omit the deltaLink altogether, silently replacing the
    // one saved moments earlier, so every run was a full walk of the drive.
    await putCursor({
        runId,
        module: "onedrive",
        subKey: SINGLE_CURSOR,
        done: true,
        scope,
        ...(deltaLink && errors === 0 ? { deltaLink } : {})
    });

    // Idempotent and network-free, like every other module's index pass —
    // rebuilt by walking the mirrored tree, so it reflects everything on
    // disk rather than only the items this particular run downloaded.
    await renderOneDriveIndex(root);

    await logger.info(`OneDrive export complete: ${files} files, ${folders} folders, ${errors} errors`);

    return { files, folders, bytes, errors };
}
